// De thema-sleutel in en uit de URL.
//
// De sleutel is `msi:<effect>|tg:<doelgroep>` en draagt dus '|' en ':'. Die
// mogen niet rauw in een padsegment, dus het segment is URL-gecodeerd. De weg
// terug is bewust ruim: `history` ontcijfert een pad met decodeURI, waardoor
// '|' al ontcijferd binnenkomt en ':' nog niet. Beide tussenvormen moeten
// dezelfde sleutel opleveren.
//
// AI-CONTEXT: See docs/features/intelligence-thema-eerst.md

// Zelfde vorm als parseThemeId in functions/intelligence/themeSnapshot.js.
const THEME_ID = /^msi:([^|]+)\|tg:(.+)$/

// Tekst die eruitziet als een waarde maar er geen is — truthy, dus elke
// falsy-guard laat hem door.
// AI-CONTEXT: See docs/pitfalls.md#database-url-sentinel-string-persisted
const SENTINELS = new Set(['undefined', 'null', 'NaN', '-'])

/**
 * @param {string} id - thema-sleutel
 * @returns {string} URL-veilig padsegment
 */
export const themeIdToSegment = (id) => encodeURIComponent(String(id || ''))

/**
 * @param {string|undefined} segment - het padsegment zoals de router het geeft
 * @returns {string|null} de sleutel, of null als het geen thema-sleutel is
 */
export const segmentToThemeId = (segment) => {
    if (typeof segment !== 'string' || !segment) return null
    let id
    try {
        id = decodeURIComponent(segment)
    } catch (e) {
        return null
    }
    const m = THEME_ID.exec(id)
    if (!m) return null
    const [, effect, doelgroep] = m
    if (SENTINELS.has(effect) || SENTINELS.has(doelgroep)) return null
    return id
}

/**
 * De twee helften van een thema-sleutel, of null. Zelfde vorm als
 * parseThemeId in functions/intelligence/themeSnapshot.js; hiermee kan een
 * pagina zijn effect-gebonden hooks starten vóór de themalijst binnen is.
 * @param {string|null|undefined} id
 * @returns {{msiId: string, targetgroup: string}|null}
 */
export const parseThemeId = (id) => {
    const m = typeof id === 'string' ? THEME_ID.exec(id) : null
    if (!m || SENTINELS.has(m[1]) || SENTINELS.has(m[2])) return null
    return { msiId: m[1], targetgroup: m[2] }
}

/**
 * @param {string} client - CompagnyID uit de URL
 * @param {string} id - thema-sleutel
 * @returns {string}
 */
export const themeDetailPath = (client, id) => `/${client}/intelligence/thema/${themeIdToSegment(id)}`

/**
 * @param {string} client - CompagnyID uit de URL
 * @param {string} insightId - QuestionInsights.ID
 * @returns {string}
 */
export const insightDetailPath = (client, insightId) => `/${client}/intelligence/inzicht/${encodeURIComponent(String(insightId || ''))}`

/**
 * @param {string} client - CompagnyID uit de URL
 * @param {string} output - Materialen-output, 'co2Kg' of 'privateEur'
 * @returns {string}
 */
export const materialBenchmarkPath = (client, output) => `/${client}/intelligence/materialen/${encodeURIComponent(String(output || ''))}`
